import { useState, useRef, useEffect, useMemo } from "react";
import { cn } from "../../lib/utils";
import { entreprisesApi } from "../../api";

function debounce(fn, ms) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  };
}

export default function EntrepriseSearch({
  label = "Rechercher une entreprise",
  name,
  onSelect,
  className,
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);

  const fetchResults = useMemo(
    () =>
      debounce(async (q) => {
        if (!q || q.length < 2) {
          setResults([]);
          setOpen(false);
          return;
        }
        setLoading(true);
        try {
          const data = await entreprisesApi.search(q);
          const items = Array.isArray(data) ? data : [];
          setResults(items);
          setOpen(items.length > 0);
          setActiveIndex(-1);
        } catch {
          // Silently fail — user can still type manually
        } finally {
          setLoading(false);
        }
      }, 300),
    []
  );

  const handleChange = (e) => {
    setQuery(e.target.value);
    fetchResults(e.target.value);
  };

  const handleSelect = (item) => {
    setQuery(item.nom_complet || "");
    setResults([]);
    setOpen(false);
    onSelect(item);
  };

  const handleKeyDown = (e) => {
    if (!open || results.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault();
      handleSelect(results[activeIndex]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={wrapperRef} className="relative space-y-1">
      {label && (
        <label
          htmlFor={name}
          className="block text-sm font-medium text-slate-700"
        >
          {label}
        </label>
      )}
      <div className="relative">
        <input
          id={name}
          name={name}
          type="text"
          value={query}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder="Nom ou SIRET..."
          autoComplete="off"
          className={cn(
            "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm",
            "focus:outline-none focus:ring-2 focus:ring-carex-500 focus:border-carex-500",
            "transition-colors duration-200",
            className
          )}
        />
        {loading && (
          <div className="absolute right-3 top-2.5">
            <div className="w-4 h-4 border-2 border-slate-300 border-t-carex-500 rounded-full animate-spin" />
          </div>
        )}
      </div>

      {open && results.length > 0 && (
        <ul className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-auto">
          {results.map((item, index) => (
            <li
              key={`${item.siret}-${index}`}
              className={cn(
                "px-3 py-2.5 text-sm cursor-pointer",
                index === activeIndex
                  ? "bg-carex-50 text-carex-700"
                  : "hover:bg-slate-50"
              )}
              onMouseEnter={() => setActiveIndex(index)}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(item);
              }}
            >
              <div className="font-medium">{item.nom_complet}</div>
              <div className="text-xs text-slate-500 mt-0.5">
                {item.siret && <span>SIRET {item.siret}</span>}
                {item.code_postal && (
                  <span className="ml-2">
                    — {item.code_postal} {item.commune}
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
